import type { StepConfig, FormValues, FormInstance, Card } from '../types';
import { getAllFieldIdsFromCondition } from './conditions';
import { findSectionForStep, collectCardFormData, clearCardFormData } from './sections';
import { getAddMoreConfig } from './addMoreConfig';

/**
 * Determines if a card should be saved before navigation
 */
export function shouldSaveCardBeforeNavigation(
  sectionInfo: ReturnType<typeof findSectionForStep>,
  nextStepInfo: ReturnType<typeof findSectionForStep> | null
): boolean {
  if (!sectionInfo?.card) return false;

  // Save current card if:
  // 1. Next step is in a different card, OR
  // 2. Next step is not in any card (like step-3-sections section list), OR
  // 3. Next step info is null (step not found in any section)
  return (
    (nextStepInfo && nextStepInfo.card && nextStepInfo.card.id !== sectionInfo.card.id) ||
    (nextStepInfo && !nextStepInfo.card) || // Next step is in a section but not in a card
    !nextStepInfo // Next step is not found in any section (like step-3-sections)
  );
}

/**
 * Determines if card save should be skipped for addMore steps
 */
export function shouldSkipCardSaveForAddMore(
  currentStep: StepConfig,
  sectionInfo: ReturnType<typeof findSectionForStep>,
  values: FormValues
): boolean {
  if (!sectionInfo?.card) return false;

  const isAddMoreStep = currentStep.addMore === true;
  const addMoreConfigForStep = getAddMoreConfig(currentStep);
  const isAddMoreCard = addMoreConfigForStep
    ? sectionInfo.card.groupKey === addMoreConfigForStep.groupKey
    : false;

  if (!isAddMoreStep || !isAddMoreCard || !addMoreConfigForStep) {
    return false;
  }

  // Check if addMore fields are all empty (form is hidden after adding)
  const areAddMoreFieldsEmpty = addMoreConfigForStep.fieldIds.every(
    (fieldId) => !values[fieldId] || values[fieldId] === ''
  );

  // Gate fields from addMoreConfig.enableCondition (e.g. hasCashGiftToSomeone, hasChildren) stay
  // set while repeatable fields are cleared after "Add". They must not force a card save or we
  // create an extra instance with only the gate field — a blank row on the dashboard.
  const enableGateFieldIds = new Set(
    getAllFieldIdsFromCondition(addMoreConfigForStep.enableCondition)
  );

  // Also check if there are any non-addMore fields with values
  // If there are non-addMore fields with values (like executorChoice), don't skip saving
  const hasNonAddMoreFieldValues = sectionInfo.card.includeFieldIds?.some((fieldId) => {
    if (addMoreConfigForStep.fieldIds.includes(fieldId)) {
      return false;
    }
    if (enableGateFieldIds.has(fieldId)) {
      return false;
    }
    return values[fieldId] !== undefined && values[fieldId] !== '';
  });

  // Only skip if addMore fields are empty AND there are no non-addMore field values
  return areAddMoreFieldsEmpty && !hasNonAddMoreFieldValues;
}

/**
 * Saves a card instance to completedForms
 */
export function saveCardInstance(
  sectionInfo: ReturnType<typeof findSectionForStep>,
  currentFormInstanceId: string,
  currentStep: StepConfig,
  stepsById: Record<string, StepConfig>,
  values: FormValues,
  setCompletedForms: React.Dispatch<React.SetStateAction<Record<string, FormInstance[]>>>
): void {
  if (!sectionInfo?.card || !currentFormInstanceId) return;

  const allFormData = collectCardFormData(sectionInfo.card, stepsById, values);
  const addMoreConfigForStep = getAddMoreConfig(currentStep);

  // Remove addMore fields from card data if this is an addMore step
  // This prevents addMore fields from being saved to the card (they're saved separately)
  if (
    addMoreConfigForStep &&
    addMoreConfigForStep.groupKey === sectionInfo.card.groupKey &&
    sectionInfo.card.id === 'card-executor'
  ) {
    // Don't save card-executor instance - addMore form handles it
    return;
  }
  if (addMoreConfigForStep) {
    addMoreConfigForStep.fieldIds.forEach((fieldId) => {
      delete allFormData[fieldId];
    });
  }

  const enableGateFieldIds = new Set(
    getAllFieldIdsFromCondition(addMoreConfigForStep?.enableCondition)
  );

  // Check if allFormData has any actual values (not just empty object)
  const hasData = Object.keys(allFormData).some(
    (key) => allFormData[key] !== undefined && allFormData[key] !== null && allFormData[key] !== ''
  );

  const hasSubstantiveDataForNewInstance =
    !addMoreConfigForStep ||
    Object.keys(allFormData).some(
      (key) =>
        allFormData[key] !== undefined &&
        allFormData[key] !== null &&
        allFormData[key] !== '' &&
        !enableGateFieldIds.has(key)
    );

  // Save completed form instance using card's groupKey
  setCompletedForms((prev) => {
    const cardInstances = prev[sectionInfo.card!.groupKey] || [];
    const existingIndex = cardInstances.findIndex(
      (inst) => inst.instanceId === currentFormInstanceId
    );

    if (existingIndex >= 0) {
      // Update existing instance - merge with existing data to preserve fields from other cards
      const updated = [...cardInstances];
      updated[existingIndex] = {
        ...updated[existingIndex],
        data: { ...updated[existingIndex].data, ...allFormData }, // Merge with existing data
        lastStepIndex: sectionInfo.stepIndex,
      };
      return { ...prev, [sectionInfo.card!.groupKey]: updated };
    } else {
      // Only create a new instance if there's actual data to save
      // This prevents creating blank instances
      if (!hasData || !hasSubstantiveDataForNewInstance) {
        return prev;
      }

      // Check if there's an existing instance in the same groupKey that we should merge with
      // This handles the case where multiple cards share the same groupKey
      // We'll create a new instance if no existing instance is found
      const newInstance: FormInstance = {
        instanceId: currentFormInstanceId,
        data: allFormData,
        lastStepIndex: sectionInfo.stepIndex,
      };
      return { ...prev, [sectionInfo.card!.groupKey]: [...cardInstances, newInstance] };
    }
  });
}

/**
 * Initializes form for a new card
 */
export function initializeCardForm(
  card: Card,
  sectionId: string,
  stepsById: Record<string, StepConfig>,
  values: FormValues,
  setters: {
    setCurrentFormInstanceId: React.Dispatch<React.SetStateAction<string | null>>;
    setCurrentSectionId: React.Dispatch<React.SetStateAction<string | null>>;
    setCurrentStepIndex: React.Dispatch<React.SetStateAction<number>>;
    setValues: React.Dispatch<React.SetStateAction<FormValues>>;
    setErrors: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>;
    setTouched: React.Dispatch<React.SetStateAction<Record<string, boolean | undefined>>>;
  }
): string {
  const instanceId = `${card.id}-${Date.now()}`;
  const clearedValues = clearCardFormData(card, stepsById, values);

  setters.setCurrentFormInstanceId(instanceId);
  setters.setCurrentSectionId(sectionId);
  setters.setCurrentStepIndex(0);
  setters.setValues(clearedValues);
  setters.setErrors({});
  setters.setTouched({});

  return instanceId;
}

/**
 * Clears form instance tracking (when navigating away from card/section)
 */
export function clearFormInstanceTracking(setters: {
  setCurrentFormInstanceId: React.Dispatch<React.SetStateAction<string | null>>;
  setCurrentSectionId: React.Dispatch<React.SetStateAction<string | null>>;
  setCurrentStepIndex: React.Dispatch<React.SetStateAction<number>>;
}): void {
  setters.setCurrentFormInstanceId(null);
  setters.setCurrentSectionId(null);
  setters.setCurrentStepIndex(0);
}

/**
 * Finds the section list step for a given section
 */
// export function findSectionListStep(sectionId: string, allSteps: StepConfig[]): StepConfig | null {
//   return allSteps.find((s) => s.sections?.some((sec) => sec.id === sectionId)) || null;
// }
